import {
  Box,
  IconButton,
  CircularProgress,
  Button,
  Typography,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCloudArrowDown,
  faFloppyDisk,
  faRotateLeft,
  faRotateRight,
} from '@fortawesome/free-solid-svg-icons';
import { WithSmallScreen } from '../../common/WithSmallScreen';

export const PreviewPanel = WithSmallScreen(
  ({
    previewRef,
    handleDownload,
    handleSave,
    isAutoSaveEnabled,
    onAutoSaveToggle,
    zip,
    busy,
    isLoading,
    undo,
    redo,
    canUndo,
    canRedo,
    isSmall,
  }) => {
    if (isLoading) {
      return (
        <Box
          sx={{
            background: '#fff',
            padding: 2,
            borderRadius: 1,
            border: '1px solid #eee',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            minHeight: 400,
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography>
            Analyzing document. This process may take a few minutes. Do not close or refresh this
            tab.
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {/* ── Pinned top: Toolbar ── */}
        <Box
          sx={{
            flex: '0 0 auto',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            p: 1,
            border: '1px solid #eee',
            borderBottom: 'none',
          }}
        >
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              onClick={undo}
              disabled={!canUndo || busy}
              size="small"
              aria-label="undo"
              title="Undo"
            >
              <FontAwesomeIcon icon={faRotateLeft} />
            </IconButton>
            <IconButton
              onClick={redo}
              disabled={!canRedo || busy}
              size="small"
              aria-label="redo"
              title="Redo"
            >
              <FontAwesomeIcon icon={faRotateRight} />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  className="myOdu__checkSwitch"
                  size="small"
                  checked={isAutoSaveEnabled}
                  onChange={(e) => onAutoSaveToggle(e.target.checked)}
                />
              }
              label="Auto Save"
              title="Auto Save"
              labelPlacement="start"
              sx={{ ml: 0, mr: 0, gap: 1 }}
            />
            <IconButton
              color="primary"
              onClick={handleSave}
              disabled={!zip || busy}
              size="small"
              title="Save"
              aria-label="save session"
            >
              <FontAwesomeIcon icon={faFloppyDisk} />
            </IconButton>

            <IconButton
              color="primary"
              onClick={handleDownload}
              disabled={!zip || busy}
              size="small"
              title="Download"
              aria-label="download document"
            >
              <FontAwesomeIcon icon={faCloudArrowDown} />
            </IconButton>
          </Box>
        </Box>

        {/* ── Scrollable: Document preview ── */}
        <Box
          ref={previewRef}
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            background: '#fff',
            borderRadius: 1,
            border: '1px solid #eee',
            color: 'black',

            // ── docx-preview overrides: make pages fluid ──
            '& .docx-wrapper': {
              background: 'transparent !important',
              padding: '0 !important',
            },
            // Each "page" rendered by docx-preview is a <section> with
            // inline fixed width/min-height. Override to fill container.
            '& .docx-wrapper > section': {
              width: '100% !important',
              minWidth: 'unset !important',
              maxWidth: '100% !important',
              boxSizing: 'border-box !important',
              padding: '24px !important',
              margin: '0 !important',
              boxShadow: 'none !important',
              // Let height be natural — remove fixed min-height
              // so short pages don't have huge whitespace
              minHeight: 'unset !important',
            },
            // Page break dividers between pages
            '& .docx-wrapper > section + section': {
              borderTop: '2px dashed #e0e0e0',
              marginTop: '8px !important',
              paddingTop: '24px !important',
            },
            // Tables and images should not overflow
            '& table': {
              maxWidth: '100% !important',
              width: 'auto !important',
              tableLayout: 'auto !important',
            },
            // docx-preview wraps images in positioned containers with inline
            // fixed dimensions. Override every layer to be fluid.
            '& img': {
              maxWidth: '100% !important',
              height: 'auto !important',
              objectFit: 'contain !important',
            },
            // Drawing wrapper divs (wp:inline / wp:anchor)
            '& .docx-wrapper div[style*="width"]': {
              maxWidth: '100% !important',
            },
            // SVG / VML shapes
            '& svg, & .vml-container': {
              maxWidth: '100% !important',
              height: 'auto !important',
            },
            // Inline drawing containers
            '& .docx_container_inline, & .docx_container_anchor': {
              maxWidth: '100% !important',
            },
          }}
        />
      </Box>
    );
  },
);
